import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "react-oidc-context";
import { Language, updateLanguage } from "@src/reducers/your-reducer";
import styles from "./dropdown-language.module.scss";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import ukFlag from "@src/assets/flags/uk.svg";
import deFlag from "@src/assets/flags/de.svg";
import cnFlag from "@src/assets/flags/cn.svg";
import itFlag from "@src/assets/flags/it.svg";
import ptBrFlag from "@src/assets/flags/pt-br.svg";
import esFlag from "@src/assets/flags/es.svg";
import dutchFlag from "@src/assets/flags/nl.svg";
import turkishFlag from "@src/assets/flags/tr.svg";
import { patchUserLanguageUseCase } from "@src/cloud-connect/user-manager/use-cases/patch-user-language-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";
import { toISO3 } from "@src/cloud-connect/user-manager/utils/to-iso3";

const cssClassName = "dropdown-language";

export const DropDownLanguage = () => {
  const i18n = useI18N();
  const auth = useAuth();
  const oidcUserId = auth.user?.profile.sub ?? null;
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.yourSlice.language);
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const languages = [
    {
      code: "en" as Language,
      label: i18n("modules.dropDownLanguage.englishLabel"),
      flag: ukFlag,
    },
    {
      code: "de" as Language,
      label: i18n("modules.dropDownLanguage.germanLabel"),
      flag: deFlag,
    },
    {
      code: "zh" as Language,
      label: i18n("modules.dropDownLanguage.chineseLabel"),
      flag: cnFlag,
    },
    {
      code: "it" as Language,
      label: i18n("modules.dropDownLanguage.italianLabel"),
      flag: itFlag,
    },
    {
      code: "es" as Language,
      label: i18n("modules.dropDownLanguage.spanishLabel"),
      flag: esFlag,
    },
    {
      code: "pt" as Language,
      label: i18n("modules.dropDownLanguage.portugueseBrazilianLabel"),
      flag: ptBrFlag,
    },
    {
      code: "nl" as Language,
      label: i18n("modules.dropDownLanguage.dutchLabel"),
      flag: dutchFlag,
    },
    {
      code: "tr" as Language,
      label: i18n("modules.dropDownLanguage.turkishLabel"),
      flag: turkishFlag,
    },
  ];

  const [open, setOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState("en");
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const popoverRef = useRef(null);
  const authSessionRef = useRef({ userId: oidcUserId, generation: 0 });
  const languageRequestIdRef = useRef(0);
  const currentLanguageRef = useRef(language);

  useLayoutEffect(() => {
    const session = authSessionRef.current;
    if (session.userId === oidcUserId) return;

    authSessionRef.current = {
      userId: oidcUserId,
      generation: session.generation + 1,
    };
    languageRequestIdRef.current += 1;
    setIsChangingLanguage(false);
    setOpen(false);
    setSelectedCode(currentLanguageRef.current ?? "en");
  }, [oidcUserId]);

  useLayoutEffect(() => {
    currentLanguageRef.current = language;
  }, [language]);

  useEffect(() => {
    if (authSessionRef.current.userId !== oidcUserId) {
      const session = authSessionRef.current;
      authSessionRef.current = {
        userId: oidcUserId,
        generation: session.generation + 1,
      };
    }

    return () => {
      const session = authSessionRef.current;
      authSessionRef.current = {
        userId: null,
        generation: session.generation + 1,
      };
      languageRequestIdRef.current += 1;
    };
  }, [oidcUserId]);

  useClickOutsideListener(popoverRef, () => setOpen(false), true);

  // Stay in sync if any other part of the app changes the language
  useEffect(() => {
    if (language && language !== selectedCode) {
      setSelectedCode(language);
    }
  }, [language, selectedCode]);

  const handleSelect = async (langCode: Language) => {
    setSelectedCode(langCode);
    setOpen(false);

    if (!oidcUserId && !authContext) {
      dispatch(updateLanguage(langCode));
      sessionStorage.setItem("user-language", langCode);
      return;
    }

    if (!oidcUserId || authContext?.userId !== oidcUserId) return;

    const userId = oidcUserId;
    const session = authSessionRef.current;
    const isCurrentSession = () =>
      authSessionRef.current.userId === userId &&
      authSessionRef.current.generation === session.generation;
    const previousLanguage = currentLanguageRef.current ?? "en";
    const requestId = ++languageRequestIdRef.current;
    setIsChangingLanguage(true);

    try {
      const response = await patchUserLanguageUseCase(
        userId,
        toISO3(langCode),
      );

      if (!isCurrentSession()) return;

      if (!response.success) {
        if (currentLanguageRef.current === previousLanguage) {
          setSelectedCode(previousLanguage);
          dispatch(updateLanguage(previousLanguage));
        }
        errorSnackbar("It was not possible to update your language.");
        return;
      }

      const currentLanguage = currentLanguageRef.current;
      if (
        currentLanguage !== previousLanguage &&
        currentLanguage !== langCode
      ) {
        return;
      }

      dispatch(updateLanguage(langCode));
      sessionStorage.setItem("user-language", langCode);
    } catch {
      if (!isCurrentSession()) return;

      if (currentLanguageRef.current === previousLanguage) {
        setSelectedCode(previousLanguage);
        dispatch(updateLanguage(previousLanguage));
      }
      errorSnackbar("It was not possible to update your language.");
    } finally {
      if (
        isCurrentSession() &&
        languageRequestIdRef.current === requestId
      ) {
        setIsChangingLanguage(false);
      }
    }
  };

  const selected =
    languages.find((l) => l.code === selectedCode) || languages[0];

  return (
    <div className={styles[cssClassName]} ref={popoverRef}>
      <button
        type="button"
        className={styles[`${cssClassName}__selector`]}
        onClick={() => setOpen(!open)}
        disabled={
          isChangingLanguage ||
          (oidcUserId ? authContext?.userId !== oidcUserId : !!authContext)
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={selected.flag}
          alt={selected.label}
          className={styles[`${cssClassName}__flag`]}
        />
        {isChangingLanguage ? (
          <SpinnerWithVariant variant={["small", "secondary"]} />
        ) : (
          <span
            className={styles[`${cssClassName}__label`]}
            title={selected.label}
          >
            {selected.label}
          </span>
        )}
      </button>

      {open && (
        <ul
          className={styles[`${cssClassName}__list`]}
          role="listbox"
          aria-activedescendant={selected.code}
          tabIndex={0}
        >
          {languages.map((lang) => (
            <li
              key={lang.code}
              id={lang.code}
              role="option"
              aria-selected={selected.code === lang.code}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__item`]}
                onClick={() => handleSelect(lang.code)}
              >
                <img
                  src={lang.flag}
                  alt={lang.label}
                  className={styles[`${cssClassName}__flag`]}
                />
                {lang.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
